import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { ArrowRight } from 'lucide-react';
import PublicNav from '../components/home/PublicNav';
import Hero from '../components/home/Hero';
import ImpactCounters from '../components/home/ImpactCounters';
import Mission from '../components/home/Mission';
import HowItWorks from '../components/home/HowItWorks';
import RoadSafety from '../components/home/RoadSafety';
import SiteFooter from '../components/home/SiteFooter';

/**
 * Public marketing homepage (route "/").
 * Phase 2: nav + hero + live-impact counters + footer + sticky mobile CTA.
 * Later phases add: responsible-citizen cards, violation storytelling,
 * how-it-works timeline, social proof, road-safety education, final CTA band.
 */
export default function Home() {
    const { isAuthenticated } = useAuth();
    const navigate = useNavigate();

    // Primary conversion action: signed-in users go to the report flow (dashboard),
    // everyone else is sent to sign in first.
    const handleReport = useCallback(() => {
        navigate(isAuthenticated ? '/dashboard' : '/login');
    }, [isAuthenticated, navigate]);

    const handleLearn = useCallback(() => {
        document.getElementById('road-safety')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, []);

    return (
        <div className="min-h-dvh bg-background flex flex-col">
            <PublicNav onReport={handleReport} />

            <main className="flex-1">
                <Hero onReport={handleReport} onLearn={handleLearn} />
                <ImpactCounters />
                <Mission />
                <HowItWorks />
                <RoadSafety />
            </main>

            <SiteFooter />

            {/* Sticky mobile conversion bar (safe-area aware) */}
            <div
                className="sm:hidden fixed inset-x-0 bottom-0 z-40 glass border-t px-4 py-3"
                style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }}
            >
                <Button className="w-full h-12 text-base" onClick={handleReport}>
                    Report a Violation
                    <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
            </div>
            {/* spacer so content isn't hidden behind the sticky bar on mobile */}
            <div className="sm:hidden h-20" aria-hidden="true" />
        </div>
    );
}
